import type { Organization, OrganizationMembershipSummary } from '@cmoh/shared'
import { Check, ChevronDown, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useSwitchMemberOrganization } from '@/hooks/use-switch-member-organization'
import { useTranslation } from '@/hooks/use-translation'
import { cn } from '@/lib/utils'

interface MemberOrgSwitcherProps {
  currentOrganization: Pick<Organization, 'id' | 'name'> | null
  memberships: OrganizationMembershipSummary[]
}

export function MemberOrgSwitcher({ currentOrganization, memberships }: MemberOrgSwitcherProps) {
  const [open, setOpen] = useState(false)
  const t = useTranslation()
  const { switchOrganization, isSwitching } = useSwitchMemberOrganization()

  const handleSelect = (organizationId: string) => {
    setOpen(false)
    if (organizationId === currentOrganization?.id) return
    switchOrganization(organizationId)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={isSwitching}
          aria-label={t('sidebar.orgSwitcher.label')}
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            'flex w-full items-center gap-2.5 rounded-[10px] bg-white/[0.08] px-2.5 py-2 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]',
            'text-sm font-medium text-white hover:bg-white/[0.14]',
            'transition-all duration-200 ease-in-out cursor-pointer',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {isSwitching && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-white" />}
          <span className="flex-1 truncate text-left text-sm font-medium">
            {currentOrganization?.name ?? t('sidebar.orgSwitcher.noOrganization')}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 opacity-60 transition-transform duration-200 ease-in-out',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) overflow-hidden rounded-xl border-0 bg-white p-1.5 shadow-[0_12px_30px_rgba(8,12,40,0.32)]"
        side="bottom"
        align="start"
        sideOffset={6}
      >
        <div className="flex max-h-[280px] flex-col gap-0.5 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {memberships.map((membership) => {
            const isCurrent = membership.id === currentOrganization?.id
            return (
              <button
                key={membership.id}
                type="button"
                aria-current={isCurrent}
                onClick={() => handleSelect(membership.id)}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors duration-200 ease-in-out hover:bg-neutral-100"
              >
                <span className="flex-1 truncate text-sm font-medium text-neutral-900">
                  {membership.name}
                </span>
                {isCurrent && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
